import Layout from '../../components/base/Layout';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const { t } = useTranslation();
  
  return (
    <Layout>
      <div className="space-y-12">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-blue-600 to-blue-800 rounded-2xl overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/f0d6efd48d85e9b4e572a0c26a4e2302.jpg"
              alt={t('home_title')}
              className="w-full h-full object-cover object-top opacity-20"
            />
          </div>
          <div className="relative px-8 py-16 text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('home_welcome')} <br />
              <span className="text-yellow-300">{t('home_title')}</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
              {t('home_description')}
            </p>
            <Link 
              to="/introducao"
              className="inline-flex items-center bg-yellow-400 text-blue-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center mr-2">
                <i className="ri-play-circle-fill"></i>
              </div>
              {t('home_start')}
            </Link>
          </div>
        </section>

        {/* Course Overview */}
        <section className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-lg mb-4">
              <i className="ri-book-open-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3">{t('home_modules')}</h3>
            <p className="text-gray-600">
              {t('home_subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-lg mb-4">
              <i className="ri-question-line text-2xl text-green-600"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3">{t('home_quizzes')}</h3>
            <p className="text-gray-600">
              {t('home_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-lg">
            <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-lg mb-4">
              <i className="ri-tools-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-xl font-semibold mb-3">{t('home_modules')}</h3>
            <p className="text-gray-600">
              {t('home_subtitle')}
            </p>
          </div>
        </section>

        {/* Course Modules */}
        <section>
          <h2 className="text-3xl font-bold text-center mb-12">{t('home_modules')}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: `1. ${t('nav_intro')}`,
                description: t('intro_description'),
                link: "/introducao",
                icon: "ri-truck-line",
                color: "blue"
              },
              {
                title: `2. ${t('nav_components')}`,
                description: t('comp_title'),
                link: "/componentes", 
                icon: "ri-settings-3-line",
                color: "green"
              },
              {
                title: `3. ${t('nav_operation')}`,
                description: t('oper_title'),
                link: "/funcionamento",
                icon: "ri-play-circle-line", 
                color: "purple"
              },
              {
                title: `4. ${t('nav_problems')}`,
                description: t('prob_title'),
                link: "/problemas",
                icon: "ri-error-warning-line",
                color: "red"
              },
              {
                title: `5. ${t('nav_diagnosis')}`, 
                description: t('diag_title'),
                link: "/diagnostico",
                icon: "ri-search-line",
                color: "orange"
              },
              {
                title: `6. ${t('nav_engineer')}`,
                description: t('eng_title'),
                link: "/engenheiro",
                icon: "ri-user-settings-line",
                color: "indigo"
              },
              {
                title: `7. ${t('nav_safety')}`,
                description: t('safety_title'),
                link: "/seguranca",
                icon: "ri-shield-check-line",
                color: "emerald"
              },
              {
                title: `8. ${t('nav_electrical')}`,
                description: t('elec_title'),
                link: "/elektrisch-schakelen",
                icon: "ri-flashlight-line",
                color: "cyan"
              }
            ].map((module, index) => (
              <Link
                key={index}
                to={module.link}
                className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow cursor-pointer group"
              >
                <div className="flex items-start space-x-4">
                  <div className={`w-12 h-12 flex items-center justify-center bg-${module.color}-100 rounded-lg group-hover:bg-${module.color}-200 transition-colors`}>
                    <i className={`${module.icon} text-2xl text-${module.color}-600`}></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold mb-2 group-hover:text-blue-600 transition-colors">
                      {module.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {module.description}
                    </p>
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-gray-400 group-hover:text-blue-600 transition-colors"></i>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Quiz Section */}
        <section className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">{t('home_quizzes')}</h2>
          <p className="text-xl mb-8">
            {t('home_description')}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/quiz1"
              className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav_quiz1')}
            </Link>
            <Link 
              to="/quiz2"
              className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav_quiz2')}
            </Link>
            <Link 
              to="/quiz3"
              className="bg-white text-green-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('nav_quiz3')}
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
}
